import { defineStore } from 'pinia'
import { nextTick, shallowRef, triggerRef } from 'vue'
import { v5, v4 } from 'uuid'
import { isError, isPlainObject } from 'lodash-es'
import { resolveActionErrorReason } from '@/lib/apiErrors'

/**
 * @typedef {Object} Message
 * @property {String} type one of [info, success, error, warning]
 * @property {String} message
 * @property {String} reason причина невозможности действия
 * @property {String} messageKey ключ локализованного сообщения со слотами
 * @property {String} emphasis безопасный фрагмент сообщения, выделяемый полужирным
 * @property {Boolean} permanent не закрывать уведомление автоматически
 * @property {Number} timeout время показа уведомления в миллисекундах
 */

const ns = {
  info: v4(),
  success: v4(),
  error: v4(),
  warning: v4(),
}

/** Стек уведомлений с группировкой одинаковых сообщений и причин ошибок. */
export const useMessageStack = defineStore('messagestack', () => {
  const stack = shallowRef(new Map())

  /**
   * Добавляет уведомление; повторения группируются по типу, тексту и причине.
   *
   * @param {string|object|Error|null} m текст, параметры уведомления или ошибка.
   * @param {string} type тип уведомления при передаче строки.
   * @returns {Promise<void>}
   */
  async function push(m = null, type = 'info') {
    await nextTick()
    let msg = { type, message: null }
    switch (true) {
      case isError(m):
        msg = { type: 'error', message: m.message }
        break
      case isPlainObject(m):
        msg = m
        break
      default:
        msg.message = m
    }
    if (msg.message === null) {
      return
    }

    // Разные причины одного действия должны оставаться отдельными уведомлениями.
    const identity = msg.reason ? JSON.stringify([msg.message, msg.reason]) : msg.message
    const id = v5(identity, ns[msg.type] || ns.info)

    if (stack.value.has(id)) {
      const v = stack.value.get(id)
      msg = { ...v, count: ++v.count }
    } else {
      msg.count = 1
    }
    stack.value.set(id, msg)
    triggerRef(stack)
  }

  /**
   * Показывает неудавшееся действие вместе с причиной из ошибки.
   *
   * @param {object|string|null} error ошибка из обработчика действия.
   * @param {string} message локализованное описание неудавшегося действия.
   * @returns {Promise<void>}
   */
  function pushError(error, message) {
    return push({ type: 'error', message, reason: resolveActionErrorReason(error, message) })
  }

  /**
   *
   * @param {String} id uuid v5
   */
  function close(id) {
    if (!stack.value.has(id)) {
      return
    }
    stack.value.delete(id)
    triggerRef(stack)
  }

  return {
    stack,
    push,
    pushError,
    close,
  }
})
